import { Button, DataTable, ErrorState, TOUCH } from "@nymbl/nos2-rules";
import { Copy, ReceiptText } from "lucide-react";
import { useMemo } from "react";
import { ApprovalChip, PaymentChip } from "@/components/expenses/StatusChips";
import { canCopy } from "@/lib/expenses/rules";
import { claimOrder, EXPENSE_COLUMNS, formatClaimDate, formatMoney } from "@/lib/expenses/view";
import { withCells } from "@/lib/table-columns";
import type { ExpenseClaimRow, ExpensesView } from "@/schemas/expenses";

interface ClaimsTableProps {
  view: ExpensesView | undefined;
  isError: boolean;
  onRetry: () => void;
  onOpen: (id: string) => void;
  onCopy: (c: ExpenseClaimRow) => void;
  copying: boolean;
  onNew: () => void;
  onYear: (year: number) => void;
}

/** Other years with claims: a year's list never hides them silently. */
function OtherYears({ years, onYear }: { years: number[]; onYear: (year: number) => void }) {
  if (years.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <span>Also in</span>
      {years.map((y) => (
        <Button
          key={y}
          variant="link"
          className={`h-auto p-0 ${TOUCH}`}
          aria-label={`Show ${y} expenses`}
          onClick={() => onYear(y)}
        >
          {y}
        </Button>
      ))}
    </div>
  );
}

const claimName = (c: ExpenseClaimRow) =>
  `${c.description}, ${c.monthName}, ${formatMoney(c.total)}`;

/** My claims for the year (spec 3). */
export function ClaimsTable({
  view,
  isError,
  onRetry,
  onOpen,
  onCopy,
  copying,
  onNew,
  onYear,
}: ClaimsTableProps) {
  const rows = useMemo(() => [...(view?.claims ?? [])].sort(claimOrder), [view?.claims]);
  const columns = useMemo(
    () =>
      withCells(EXPENSE_COLUMNS, {
        submitted: { render: (c) => (c.dateSubmitted ? formatClaimDate(c.dateSubmitted) : "") },
        description: {
          className: "max-w-80",
          render: (c) => (
            <>
              <Button
                variant="link"
                className={`h-auto justify-start p-0 text-left whitespace-normal ${TOUCH}`}
                aria-label={`Open ${claimName(c)}`}
                onClick={() => onOpen(c.id)}
              >
                {c.description}
              </Button>
              {c.po && <div className="text-xs text-muted-foreground">{c.po}</div>}
            </>
          ),
        },
        approval: {
          render: (c) => <ApprovalChip status={c.approval} noApprover={c.noApprover} />,
        },
        payment: { render: (c) => <PaymentChip status={c.payment} approval={c.approval} /> },
        estPaid: { render: (c) => (c.estPaidOn ? formatClaimDate(c.estPaidOn) : "") },
        amount: { render: (c) => formatMoney(c.total) },
      }),
    [onOpen],
  );

  return (
    <DataTable
      label="My expenses"
      title="My expenses"
      search={{ placeholder: "Search expenses…" }}
      columns={columns}
      rows={rows}
      rowKey={(c) => c.id}
      loading={!view && !isError}
      error={isError && !view ? new Error("Expenses didn't load") : undefined}
      onRetry={onRetry}
      notice={
        view && (
          <>
            {isError && <ErrorState message="Expenses didn't load" onRetry={onRetry} />}
            <OtherYears years={view.otherYears} onYear={onYear} />
          </>
        )
      }
      empty={{
        title: view ? `No expenses in ${view.year}` : "No expenses",
        icon: ReceiptText,
        action: (
          <Button className={TOUCH} onClick={onNew}>
            New expense
          </Button>
        ),
      }}
      rowActions={(c) =>
        canCopy(c.typeEnum) && (
          <Button
            variant="ghost"
            size="icon"
            className={TOUCH}
            aria-label={`Copy ${claimName(c)}`}
            disabled={copying}
            onClick={() => onCopy(c)}
          >
            <Copy aria-hidden="true" />
          </Button>
        )
      }
    />
  );
}
